import React, { useMemo, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { Button, IconButton, FormControl, InputLabel, NativeSelect } from '@mui/material';
import { CameraAlt, Videocam, Mic, Stop } from '@mui/icons-material';
import { API_ROOT } from '../../../../../baseUrls';
import AudioRecorder from './AudioRecorder.js';
import VideoRecorder from './VideoRecorder.js';
import FolderSelector from '../../folders/FolderSelector.js';

async function uploadBase64({ type, base64, mindfileId, authenticity_token, emotion, folderId, fetchItems }) {
  console.log("Upload base64")
  const formData = new FormData();
  formData.append('recording[type]', type); // Photo | Video | Audio
  formData.append('recording[emotion]', emotion);
  if(folderId){
    formData.append("folder_id", folderId);
  }
  formData.append('data64', base64);
  await fetch(`${API_ROOT}/mindfiles/${mindfileId}/upload-recording`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
    headers: { 'X-CSRF-Token': authenticity_token }
  })
    .then(fetchItems)
    .catch(console.error);
}

// Upload a Blob as form-data (same "data" field).
async function uploadBlob({ type, blob, emotion, folderId, ext, mindfileId, authenticity_token, fetchItems }) {
  console.log("Upload blob", blob)
  const formData = new FormData();
  formData.append('recording[type]', type); // Video | Audio
  if(ext) formData.append('recording[ext]', ext); // Video | Audio
  formData.append('recording[emotion]', emotion);
  if(folderId){
    formData.append("folder_id", folderId);
  }
  formData.append('fileData', blob, `recording.${ext}`);
  await fetch(`${API_ROOT}/mindfiles/${mindfileId}/upload-recording`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
    headers: { 'X-CSRF-Token': authenticity_token }
  })
    .then(fetchItems)
    .catch(console.error);
}

async function capturePhoto(webcamRef, mindfileId, authenticity_token, emotion, folderId, fetchItems, callback) {
  let imgBase64 = webcamRef.current.getScreenshot();
  if (imgBase64) {
    imgBase64 = imgBase64.replace('data:image/jpeg;base64,', '');
    await uploadBase64({ type: 'Photo', base64: imgBase64, mindfileId, authenticity_token, emotion, folderId, fetchItems })
  }
}

const styles = {
  buttonRow: {
    
  }
};

export default function Camera({
  webcamRef,
  emotion,
  handleEmotionChange,
  canEdit,
  mindfileId,
  authenticity_token,
  fetchItems,
  folderList,
  addFolder,
}) {
  // const [mode, setMode] = useState('Audio'); // Photo | Video | Audio
  const [playbackMediaUrl, setPlaybackMediaUrl] = useState(null);
  const [recording, setRecording] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const folderId = selectedFolder ? selectedFolder.id : null;
  console.log({folderId})
  // Toggle microphone usage on the <Webcam> when not in Audio-only mode.
  // const webcamAudio = mode === 'Video';

  return !canEdit ? null : (
    <div style={{ display: 'flex', flex: 1, flexDirection: 'column', justifyContent: 'center', alignItems: "center", gap: '0.5rem' }}>
      <div >
        <div style={playbackMediaUrl ? {display: 'none'} : {}}>
          <Webcam
            style={{maxWidth: '100%', maxHeight: 'calc(95dvh - 65px - 0.5rem)'}}
            ref={webcamRef}
            imageSmoothing={false}
            mirrored={true}
            screenshotFormat="image/jpeg"
            screenshotQuality={0.8}
            onUserMediaError={(e) => {
              console.error('Camera/Mic error:', e);
            }}
          />
        </div>
        {!playbackMediaUrl ? null : 
          <div style={{width: '100%', height: '100%', display: "flex", alignItems: "center", justifyContent: 'center'}}>
            <video style={{width: '100%'}} src={playbackMediaUrl} controls autoPlay loop={false} />
          </div>
        }
      </div>
      <div style={{display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap-reverse', width: '100%', justifyContent: 'center' }}>
        <div style={{display: 'flex', gap: '1rem', alignItems: 'center', margin: '0.5rem 0', flexWrap: 'wrap' }}>
          <FormControl>
            <InputLabel>Emotion</InputLabel>
            <NativeSelect value={emotion} onChange={handleEmotionChange} inputProps={{ name: 'emotion', id: 'emotion-native-select' }}>
              <option value="Angry">Angry</option>
              <option value="Annoyed">Annoyed</option>
              <option value="Ecstatic">Ecstatic</option>
              <option value="Happy">Happy</option>
              <option value="Neutral">Neutral</option>
              <option value="Pleased">Pleased</option>
              <option value="Sad">Sad</option>
              <option value="Scared">Scared</option>
              <option value="Surprised">Surprised</option>
            </NativeSelect>
          </FormControl>
        </div>

        {playbackMediaUrl ?
          <div style={{minHeight: '65px', minWidth: '122px', display: 'flex'}}>
            <Button style={{marginTop: 'auto'}} onClick={() => setPlaybackMediaUrl(null)}>Close Preview</Button>
          </div>
          : null
        }
        <div style={playbackMediaUrl ? {display: 'none'} : {display: 'flex',flexDirection: 'column', gap: '1rem', alignItems: 'center'}}>
          <div style={{display: 'flex', gap: '1rem', alignItems: 'center'}}>
            {recording === "Video" ? "Max 15 minutes" : recording === "Audio" ? "Max 60 minutes" : "Start Recording"}
          </div>
          <div style={{display: 'flex', gap: '1rem', alignItems: 'center'}}>
            <div style={!recording || recording === "Audio" ? {} : {opacity: 0}}>
              <AudioRecorder
                onStart={() => setRecording("Audio")}
                handleData={async (contentType, ext, bytes) => {
                  const blob = new Blob([bytes], { type: contentType });
                  console.log({ contentType, ext, size: bytes.length, emotion });
                  const url = URL.createObjectURL(blob);
                  setPlaybackMediaUrl(url)
                  await uploadBlob({ type: "Audio", blob, emotion, folderId, ext, mindfileId, authenticity_token, fetchItems })
                    .then(() => setRecording(null))
                }}
              />
            </div>
            <div style={!recording || recording === "Photo" ? {} : {opacity: 0}}>
              <FormControl>
                <IconButton
                  color="primary"
                  disabled={recording}
                  onClick={() => {
                    setRecording("Photo")
                    capturePhoto(webcamRef, mindfileId, authenticity_token, emotion, folderId, fetchItems)
                      .then(() => setRecording(null))
                  }}
                >
                  <CameraAlt />
                </IconButton>
              </FormControl>
            </div>
            <div style={!recording || recording === "Video" ? {} : {opacity: 0}}>
              <VideoRecorder 
                onStart={() => setRecording("Video")}
                onStop={(mediaBlogUrl, blob) => {
                  console.log("set url", mediaBlogUrl)
                  setPlaybackMediaUrl(mediaBlogUrl)
                  setRecording(null)
                }}
                mindfileId={mindfileId}
                authenticity_token={authenticity_token}
                fetchItems={fetchItems}
                emotion={emotion}
                folderId={folderId}
              />
            </div>
          </div>
        </div>

      </div>
          <div style={{width: '350px', marginBottom: '0.5rem'}}>
            <FolderSelector
              label="Folder"
              margin="none"
              selectedFolder={selectedFolder}
              folderList={folderList}
              addFolder={addFolder}
              handleFolderSelect={setSelectedFolder}
            />
          </div>
    </div>
  );
}
